/**
 * Settings for agents (admins): what agents should know, AI provider keys
 * (write only, last 4 shown), and the agents with this month's usage and cost.
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { ErrorNote, Fold, Modal } from "../components/ui";
import { loadSessions, useWork } from "../lib/work";
import { api, ApiError } from "../lib/api";
import { squareImage } from "../lib/image";

interface Provider {
  id: string;
  label: string;
  example: string;
  custom: boolean;
  keyEnv: string;
  configured: boolean;
  source: "settings" | "server" | null;
  last4: string | null;
}
interface Usage {
  requests: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  costKnown: boolean;
}
interface AgentRow {
  id: string;
  name: string;
  avatarUrl: string | null;
  kind: "claude_channel" | "api" | "codex_cli";
  onPlan: boolean;
  provider: string | null;
  providerLabel: string;
  model: string | null;
  instructions: string;
  enabled: boolean;
  baseUrl: string | null;
  priceInPerM: number | null;
  priceOutPerM: number | null;
  monthlyBudgetUsd: number | null;
  rooms: number;
  online: boolean;
  usage: Usage;
}
interface ModelOption {
  id: string;
  priceInPerM: number | null;
  priceOutPerM: number | null;
}
interface Data {
  secretsEnabled: boolean;
  brief: string;
  providers: Provider[];
  agents: AgentRow[];
  coordinator: Coordinator;
}
interface Coordinator {
  id: string | null;
  avatarUrl: string | null;
  name: string;
  about: string;
  settings: { maxRunning: number; maxRounds: number; review: "chatgpt" | "agent" | "none"; reviewerId: string | null };
  workerRuns: number;
  workerMinutes: number;
  events: number;
}

const money = (n: number) => `$${n < 1 ? n.toFixed(4) : n.toFixed(2)}`;
const tokens = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${Math.round(n / 1000)}k` : String(n));

export function AgentSettings() {
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AgentRow | "new" | null>(null);
  const reload = () =>
    api<Data>("/api/settings/agents").then(setData, (err) => setError(err instanceof ApiError ? err.message : "Could not load agents."));
  useEffect(() => void reload(), []);

  if (error) return <ErrorNote error={error} />;
  if (!data) return <p className="muted-text">Loading</p>;
  return (
    <>
      <Fold
        id="settings-agents"
        anchor="agents"
        title="Agents"
        summary={`${data.agents.length} ${data.agents.length === 1 ? "agent" : "agents"}, ${data.agents.filter((a) => a.enabled).length} on: ${data.agents.map((a) => a.name).join(", ")}`}
        side={
          <button className="btn primary small" type="button" onClick={() => setEditing("new")}>
            Add an agent
          </button>
        }
      >
        <p className="muted-text">Put an agent in a room from the room panel. Anyone in the room can @mention an API key agent; Claude answers only you.</p>
        <ul className="rows">
          {data.agents.map((a) => (
            <AgentLine key={a.id} agent={a} onEdit={() => setEditing(a)} onChanged={reload} />
          ))}
          <CoordinatorLine c={data.coordinator} agents={data.agents} onChanged={reload} />
        </ul>
      </Fold>

      <BriefEditor initial={data.brief} />
      <KeysEditor providers={data.providers} enabled={data.secretsEnabled} onChanged={reload} />

      {editing && editing !== "new" && editing.onPlan ? (
        <PlanAgentForm agent={editing} onClose={() => setEditing(null)} onSaved={() => (setEditing(null), void reload())} />
      ) : (
        editing && <AgentForm agent={editing === "new" ? null : editing} providers={data.providers} onClose={() => setEditing(null)} onSaved={() => (setEditing(null), void reload())} />
      )}
    </>
  );
}

/** Claude on the Agent SDK (Phase 8b): which login it runs on, and its sessions. */
function ClaudeSessions() {
  const sessions = useWork((w) => w.sessions);
  const auth = useWork((w) => w.auth);
  const online = useWork((w) => w.online);
  const reason = useWork((w) => w.reason);
  useEffect(() => {
    void loadSessions().catch(() => undefined);
  }, []);
  const list = Object.values(sessions).filter((s) => s.kind !== "quick").sort((a, b) => (b.lastTurnAt ?? "").localeCompare(a.lastTurnAt ?? ""));
  return (
    <>
      <small className={online ? "claude-auth ok" : "claude-auth bad"}>
        {online ? `Runs on the Claude Agent SDK, signed in with ${auth?.subscriptionType ?? "your plan"}${auth?.email ? ` (${auth.email})` : ""}, no API key.` : `Not running: ${reason || "the Claude container is not connected"}.`}
        {auth?.sdk ? ` SDK ${auth.sdk}.` : ""}
      </small>
      {list.length > 0 && (
        <small>
          Sessions:{" "}
          {list.map((s, i) => (
            <span key={s.id}>
              {i > 0 && ", "}
              <Link to={`/work/${s.id}`}>{s.title}</Link>
              {s.openPrompts ? ` (${s.openPrompts} waiting)` : s.busy ? " (working)" : s.live ? " (open)" : ""}
            </span>
          ))}
        </small>
      )}
    </>
  );
}

function AgentLine({ agent: a, onEdit, onChanged }: { agent: AgentRow; onEdit: () => void; onChanged: () => void }) {
  const [test, setTest] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const claude = a.kind === "claude_channel";
  const codex = a.kind === "codex_cli";
  const plan = claude ? "Max" : "ChatGPT";
  const cost = a.usage.requests === 0 ? "no answers yet" : a.usage.costKnown ? `${money(a.usage.costUsd)} estimated` : "set prices to see the cost";
  return (
    <li className="agent-line">
      <AgentPicture agent={a} onChanged={onChanged} />
      <div className="agent-info">
        <b>
          {a.name} {!a.enabled && <span className="chip">off</span>}
          {a.onPlan && <span className="chip">{a.online ? "online" : codex ? "not signed in" : "offline"}</span>}
        </b>
        <small>
          {a.providerLabel}
          {!a.onPlan && a.model && ` · ${a.model}`}
          {codex && ` · ${a.model || "Codex default model"}`}
          {claude && ` · ${a.model ? a.model[0]!.toUpperCase() + a.model.slice(1) : "plan default model"}`} · in {a.rooms} {a.rooms === 1 ? "room" : "rooms"}
        </small>
        {a.onPlan && (
          <small>
            This month: {a.usage.requests} {a.usage.requests === 1 ? "answer" : "answers"} on your {plan} plan, no API cost. Only you can @mention it.
          </small>
        )}
        {!a.onPlan && (
          <small>
            This month: {a.usage.requests} {a.usage.requests === 1 ? "answer" : "answers"}, {tokens(a.usage.inputTokens + a.usage.outputTokens)} tokens, {cost}
            {a.monthlyBudgetUsd != null && ` of ${money(a.monthlyBudgetUsd)} budget`}
          </small>
        )}
        {test && <small className="test-result">{test}</small>}
        {claude && <ClaudeSessions />}
      </div>
      <div className="line-actions">
        {a.onPlan && (
          <button className="btn ghost small" type="button" onClick={onEdit}>
            Edit
          </button>
        )}
        {!a.onPlan && (
          <>
            <button
              className="btn ghost small"
              type="button"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setTest("Testing");
                const r = await api<{ ok: boolean; text: string }>(`/api/settings/agents/${a.id}/test`, {}).catch((err) => ({ ok: false, text: err instanceof ApiError ? err.message : "failed" }));
                setTest(r.ok ? `Test passed: the model answered "${r.text}".` : `Test failed: ${r.text}.`);
                setBusy(false);
              }}
            >
              Test
            </button>
            <button className="btn ghost small" type="button" onClick={onEdit}>
              Edit
            </button>
          </>
        )}
        <button
          type="button"
          className="toggle"
          role="switch"
          aria-checked={a.enabled}
          aria-label={`${a.name} on or off`}
          onClick={async () => {
            await api(`/api/settings/agents/${a.id}`, { enabled: !a.enabled }, "PATCH");
            onChanged();
          }}
        />
      </div>
    </li>
  );
}

const REVIEW_LABEL = { chatgpt: "ChatGPT on your plan", agent: "an API key agent", none: "nobody (you look yourself)" } as const;

/** The built in Coordinator: its picture, what it does and did, and Edit for its settings. */
function CoordinatorLine({ c, agents, onChanged }: { c: Coordinator; agents: AgentRow[]; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const reviewer = c.settings.review === "agent" ? (agents.find((a) => a.id === c.settings.reviewerId)?.name ?? "an API key agent") : REVIEW_LABEL[c.settings.review];
  return (
    <li className="agent-line">
      {c.id ? <AgentPicture agent={{ id: c.id, name: c.name, avatarUrl: c.avatarUrl }} onChanged={onChanged} /> : <div className="agent-picture" />}
      <div className="agent-info">
        <b>
          {c.name} <span className="chip">built in</span>
        </b>
        <small>
          Agora's own agent: runs the coding workers and posts project events. Fixed rules, no model of its own. Reviews by {reviewer} · {c.settings.maxRunning} {c.settings.maxRunning === 1 ? "worker" : "workers"} at a time · up to {c.settings.maxRounds}{" "}
          {c.settings.maxRounds === 1 ? "round" : "rounds"} per task
        </small>
        <small>
          This month: {c.workerRuns} coding worker {c.workerRuns === 1 ? "run" : "runs"} ({c.workerMinutes} GitHub minutes), {c.events} project {c.events === 1 ? "event" : "events"}.
        </small>
      </div>
      <div className="line-actions">
        <button className="btn ghost small" type="button" onClick={() => setEditing(true)}>
          Edit
        </button>
      </div>
      {editing && <CoordinatorForm c={c} agents={agents} onClose={() => setEditing(false)} onSaved={() => (setEditing(false), onChanged())} />}
    </li>
  );
}

function CoordinatorForm({ c, agents, onClose, onSaved }: { c: Coordinator; agents: AgentRow[]; onClose: () => void; onSaved: () => void }) {
  const apiAgents = agents.filter((a) => a.kind === "api");
  const [name, setName] = useState(c.name);
  const [reviewer, setReviewer] = useState(c.settings.review === "agent" && c.settings.reviewerId ? c.settings.reviewerId : c.settings.review);
  const [maxRunning, setMaxRunning] = useState(c.settings.maxRunning);
  const [maxRounds, setMaxRounds] = useState(c.settings.maxRounds);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={`Edit ${c.name}`} onClose={onClose} wide>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          const review = reviewer === "chatgpt" || reviewer === "none" ? reviewer : "agent";
          try {
            await api("/api/settings/coordinator", { name, maxRunning, maxRounds, review, reviewerId: review === "agent" ? reviewer : null }, "PATCH");
            onSaved();
          } catch (err) {
            setError(err instanceof ApiError ? err.message : "Could not save.");
            setBusy(false);
          }
        }}
      >
        <ErrorNote error={error} />
        <label className="field">
          <span>Name (what its messages show)</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} required />
        </label>
        <label className="field">
          <span>Who reviews the pull requests</span>
          <select value={reviewer} onChange={(e) => setReviewer(e.target.value)}>
            <option value="chatgpt">ChatGPT on your plan (no API cost)</option>
            {apiAgents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.providerLabel}{a.model ? `, ${a.model}` : ""}; cost counted in Usage)
              </option>
            ))}
            <option value="none">Nobody: I look at each pull request myself</option>
          </select>
        </label>
        <div className="field-row">
          <label className="field">
            <span>Coding workers at a time</span>
            <select value={maxRunning} onChange={(e) => setMaxRunning(Number(e.target.value))}>
              {[1, 2, 3].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Rounds per task (build plus send backs)</span>
            <select value={maxRounds} onChange={(e) => setMaxRounds(Number(e.target.value))}>
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
        <small className="muted-text">Its picture: click Picture in the list. Workers run on your Max plan and use GitHub minutes, so fewer at a time and fewer rounds keep the use low.</small>
        <details className="how-built">
          <summary>How the Coordinator is built</summary>
          <p>
            It is code in Agora (a fixed state machine), not an AI: it never decides on its own, it follows these steps and only on your clicks.
          </p>
          <ol>
            <li>
              <b>Approve</b> on a code task (or an agent's card you confirm): it starts the project's worker workflow on GitHub (<code>agora-worker.yml</code> in the repo), on branch <code>agora/task-…</code>, with the task, its notes, the project's
              decisions and the room's recent messages.
            </li>
            <li>The worker is Claude Code (Opus) on your Max plan, in GitHub's machine: file and git tools, the project's tests and a headless browser on a local preview, 80 turns, 50 minutes. It commits; the workflow pushes the branch.</li>
            <li>When the run ends, GitHub tells Agora (webhook). With new commits, the Coordinator opens the pull request with Agora's GitHub App, so the repo's tests run.</li>
            <li>It sends the diff to the reviewer you pick above and posts the review on the pull request and in the room. The builder never reviews its own work.</li>
            <li>
              <b>Send back</b>: a new round on the same pull request with the review and your note. <b>Confirm</b>: on an automatic project with tests passing it merges; the project's deploy makes it live.
            </li>
          </ol>
          <p>
            Safety rules it never breaks: it does not merge a manual project, nor changes to workflows, deploy scripts, Dockerfiles, compose files, migrations or .env files; it says what to do instead. A run with no report for 45 minutes counts as stopped. Every step, and every problem with what to do, is
            posted in the task's rooms.
          </p>
          <p>
            Where things are set: Coding workers on or off and the delivery mode in each project's Edit; the Max token as the repo secret <code>CLAUDE_CODE_OAUTH_TOKEN</code>; GitHub permissions in Settings, GitHub; the rest here.
          </p>
        </details>
        <div className="actions">
          <button className="btn ghost" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="submit" disabled={busy || !name.trim()}>
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}

function AgentPicture({ agent: a, onChanged }: { agent: Pick<AgentRow, "id" | "name" | "avatarUrl">; onChanged: () => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="agent-picture">
      <button type="button" className="picture-btn" onClick={() => input.current?.click()} title="Change the picture" aria-label={`Change the picture of ${a.name}`}>
        <span className="avatar agent" style={{ width: 40, height: 40 }} aria-hidden="true">
          {a.avatarUrl ? <img src={a.avatarUrl} alt="" /> : <span className="avatar-letters">{a.name.slice(0, 2).toUpperCase()}</span>}
        </span>
        <span className="picture-hint">Picture</span>
      </button>
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (!file) return;
          setError(null);
          try {
            await api(`/api/avatars/${a.id}`, { dataUrl: await squareImage(file) }, "PUT");
            onChanged();
          } catch (err) {
            setError(err instanceof Error ? err.message : "Could not upload.");
          }
        }}
      />
      {error && <small className="picture-error">{error}</small>}
    </div>
  );
}

function BriefEditor({ initial }: { initial: string }) {
  const [brief, setBrief] = useState(initial);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Fold id="settings-brief" anchor="brief" title="What agents should know" summary={brief.trim() ? `${brief.length} characters, sent with every question.` : "Not written yet."}>
      <p className="muted-text">Sent with every question to Claude and to every agent. Write who you are, your companies and apps, the team, current priorities, words you use. Room members see the answers, so keep private details out.</p>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          try {
            await api("/api/settings/brief", { brief }, "PUT");
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
          } catch (err) {
            setError(err instanceof ApiError ? err.message : "Could not save.");
          }
        }}
      >
        <ErrorNote error={error} />
        <textarea className="brief" rows={10} value={brief} onChange={(e) => setBrief(e.target.value)} maxLength={12000} aria-label="What agents should know" placeholder="For example: I am Sergio. I run SolutionsGuru (AI services for realtors and loan officers) and Stark Processing (loan processing)..." />
        <div className="actions">
          <small className="muted-text">{brief.length} of 12000 characters</small>
          <button className="btn primary small" type="submit">
            {saved ? "Saved" : "Save"}
          </button>
        </div>
      </form>
    </Fold>
  );
}

function KeysEditor({ providers, enabled, onChanged }: { providers: Provider[]; enabled: boolean; onChanged: () => void }) {
  return (
    <Fold id="settings-keys" anchor="keys" title="AI provider keys" summary={`${providers.filter((p) => !p.custom && p.configured).length} of ${providers.filter((p) => !p.custom).length} providers have a key: ${providers.filter((p) => !p.custom && p.configured).map((p) => p.label.replace(/ \(.*\)$/, "")).join(", ") || "none yet"}.`}>
      <p className="muted-text">Paste a key once. It is stored encrypted and never shown again, only its last 4 characters. Keys set on the server keep working too.</p>
      {!enabled && <div className="notice info">The server is not set up to store keys yet.</div>}
      <ul className="rows">
        {providers
          .filter((p) => !p.custom)
          .map((p) => (
            <KeyLine key={p.id} provider={p} enabled={enabled} onChanged={onChanged} />
          ))}
      </ul>
    </Fold>
  );
}

function KeyLine({ provider: p, enabled, onChanged }: { provider: Provider; enabled: boolean; onChanged: () => void }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const status = p.source === "settings" ? `Set here, ends in ${p.last4}` : p.source === "server" ? "Set on the server" : "Not set";
  return (
    <li className="key-line">
      <div>
        <b>{p.label}</b>
        <small>
          <span className={`chip${p.configured ? " ok" : ""}`}>{status}</span>
        </small>
        <ErrorNote error={error} />
      </div>
      <form
        className="key-form"
        onSubmit={async (e: FormEvent) => {
          e.preventDefault();
          setError(null);
          try {
            await api(`/api/settings/keys/${p.id}`, { apiKey: value }, "PUT");
            setValue("");
            onChanged();
          } catch (err) {
            setError(err instanceof ApiError ? err.message : "Could not save the key.");
          }
        }}
      >
        <input type="password" autoComplete="off" value={value} onChange={(e) => setValue(e.target.value)} placeholder={p.source === "settings" ? "Paste a new key to replace it" : "Paste the key"} aria-label={`${p.label} key`} disabled={!enabled} />
        <button className="btn primary small" type="submit" disabled={!enabled || value.trim().length < 8}>
          Save
        </button>
        {p.source === "settings" && (
          <button
            className="btn ghost small"
            type="button"
            onClick={async () => {
              if (!confirm(`Remove the ${p.label} key?`)) return;
              await api(`/api/settings/keys/${p.id}`, undefined, "DELETE");
              onChanged();
            }}
          >
            Remove
          </button>
        )}
      </form>
    </li>
  );
}

interface PlanModel {
  id: string;
  label: string;
  description?: string;
}

/** Claude (Max plan) and ChatGPT (ChatGPT plan): name, model from the plan, role. */
function PlanAgentForm({ agent, onClose, onSaved }: { agent: AgentRow; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(agent.name);
  const [model, setModel] = useState(agent.model ?? "");
  const [instructions, setInstructions] = useState(agent.instructions);
  const [models, setModels] = useState<PlanModel[] | null>(null);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const claude = agent.kind === "claude_channel";

  useEffect(() => {
    api<{ models: PlanModel[]; error?: string }>(`/api/settings/agents/${agent.id}/models`).then(
      (r) => (setModels(r.models), setModelsError(r.error ?? (r.models.length <= 1 ? "the plan did not list its models yet" : null))),
      (err) => setModelsError(err instanceof ApiError ? err.message : "Could not load the models."),
    );
  }, [agent.id]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api(`/api/settings/agents/${agent.id}`, { name, model, instructions }, "PATCH");
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save.");
      setBusy(false);
    }
  }

  const known = models?.some((m) => m.id === model);
  const picked = models?.find((m) => m.id === model);
  return (
    <Modal title={`Edit ${agent.name}`} onClose={onClose} wide>
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        <label className="field">
          <span>Name (people write @ and this name)</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} required />
        </label>
        <label className="field">
          <span>Model on your {claude ? "Max" : "ChatGPT"} plan</span>
          {models && models.length > 1 ? (
            <select value={known ? model : "__current"} onChange={(e) => setModel(e.target.value === "__current" ? (agent.model ?? "") : e.target.value)}>
              {!known && <option value="__current">{model}</option>}
              {models.map((m) => (
                <option key={m.id || "default"} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          ) : (
            <input value={model} onChange={(e) => setModel(e.target.value)} placeholder={claude ? "Empty: the plan default" : "Empty: the Codex default"} />
          )}
          <small>
            {!models && !modelsError && "Loading the models of your plan."}
            {modelsError && `The list is not available (${modelsError}). Type the model name instead.`}
            {picked?.description}
            {claude && models && " Running sessions switch to it at once."}
            {" No API cost: it runs on your plan."}
          </small>
        </label>
        <label className="field">
          <span>Role and instructions (optional)</span>
          <textarea rows={5} value={instructions} onChange={(e) => setInstructions(e.target.value)} maxLength={4000} placeholder="For example: Brand strategist. Answer with three options and a recommendation." />
        </label>
        <div className="actions">
          <button className="btn ghost" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="submit" disabled={busy}>
            {busy ? "Saving" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function AgentForm({ agent, providers, onClose, onSaved }: { agent: AgentRow | null; providers: Provider[]; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(agent?.name ?? "");
  const [provider, setProvider] = useState(agent?.provider ?? providers.find((p) => p.configured && !p.custom)?.id ?? "openai");
  const [model, setModel] = useState(agent?.model ?? "");
  const [instructions, setInstructions] = useState(agent?.instructions ?? "");
  const [baseUrl, setBaseUrl] = useState(agent?.baseUrl ?? "");
  const [priceIn, setPriceIn] = useState(agent?.priceInPerM?.toString() ?? "");
  const [priceOut, setPriceOut] = useState(agent?.priceOutPerM?.toString() ?? "");
  const [budget, setBudget] = useState(agent?.monthlyBudgetUsd?.toString() ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const p = providers.find((x) => x.id === provider);
  const num = (s: string) => (s.trim() === "" ? null : Number(s));
  const [models, setModels] = useState<ModelOption[] | null>(null);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    let live = true;
    setModels(null);
    setModelsError(null);
    if (p?.custom && !baseUrl) return;
    api<{ models: ModelOption[]; error?: string }>(`/api/settings/providers/${provider}/models${p?.custom ? `?baseUrl=${encodeURIComponent(baseUrl)}` : ""}`).then(
      (r) => {
        if (!live) return;
        setModels(r.models);
        setModelsError(r.error ?? null);
        if (r.models.length && model && !r.models.some((m) => m.id === model)) setTyping(true);
      },
      (err) => live && setModelsError(err instanceof ApiError ? err.message : "Could not load the models."),
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, p?.custom ? baseUrl : ""]);

  function pickModel(id: string) {
    if (id === "__other") return setTyping(true);
    setModel(id);
    const m = models?.find((x) => x.id === id);
    if (m?.priceInPerM != null) setPriceIn(String(m.priceInPerM));
    if (m?.priceOutPerM != null) setPriceOut(String(m.priceOutPerM));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = {
      name,
      provider,
      model,
      instructions,
      baseUrl: p?.custom ? baseUrl || null : null,
      priceInPerM: num(priceIn),
      priceOutPerM: num(priceOut),
      monthlyBudgetUsd: num(budget),
    };
    try {
      if (agent) await api(`/api/settings/agents/${agent.id}`, body, "PATCH");
      else await api("/api/settings/agents", body);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save.");
      setBusy(false);
    }
  }

  return (
    <Modal title={agent ? `Edit ${agent.name}` : "Add an agent"} onClose={onClose} wide>
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        <label className="field">
          <span>Name (people write @ and this name)</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} required placeholder="For example GPT, Gemini, Writer" />
        </label>
        <label className="field">
          <span>Provider</span>
          <select value={provider} onChange={(e) => setProvider(e.target.value)}>
            {providers.map((x) => (
              <option key={x.id} value={x.id}>
                {x.label}
                {x.configured ? "" : " (no key yet)"}
              </option>
            ))}
          </select>
          {p && !p.configured && <small>Add its key below in AI provider keys before using it.</small>}
        </label>
        {p?.custom && (
          <label className="field">
            <span>Base URL</span>
            <input type="url" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="http://ollama:11434/v1" required />
          </label>
        )}
        <label className="field">
          <span>Model</span>
          {models && models.length > 0 && !typing ? (
            <select value={models.some((m) => m.id === model) ? model : ""} onChange={(e) => pickModel(e.target.value)} required>
              <option value="" disabled>
                Choose one of the {models.length} models this key can use
              </option>
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.id}
                  {m.priceInPerM != null && m.priceOutPerM != null ? `  ·  $${m.priceInPerM} in, $${m.priceOutPerM} out per 1M` : ""}
                </option>
              ))}
              <option value="__other">Another model, type its name</option>
            </select>
          ) : (
            <input value={model} onChange={(e) => setModel(e.target.value)} required placeholder={p ? `For example ${p.example}` : ""} />
          )}
          <small>
            {!models && !modelsError && "Loading the models this key can use."}
            {modelsError && `The list is not available (${modelsError}). Type the model name instead.`}
            {models && !modelsError && (typing ? (
              <button type="button" className="linkish" onClick={() => setTyping(false)}>
                Back to the list
              </button>
            ) : "Picking a model fills in its prices when they are known. You can still change them.")}
          </small>
        </label>
        <label className="field">
          <span>Role (optional)</span>
          <textarea rows={3} value={instructions} onChange={(e) => setInstructions(e.target.value)} maxLength={4000} placeholder="For example: you write marketing captions in our house style, short and plain." />
        </label>
        <div className="grid3">
          <label className="field">
            <span>Price per 1M input tokens, USD</span>
            <input inputMode="decimal" value={priceIn} onChange={(e) => setPriceIn(e.target.value)} placeholder="optional" />
          </label>
          <label className="field">
            <span>Price per 1M output tokens, USD</span>
            <input inputMode="decimal" value={priceOut} onChange={(e) => setPriceOut(e.target.value)} placeholder="optional" />
          </label>
          <label className="field">
            <span>Monthly budget, USD</span>
            <input inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="no limit" />
          </label>
        </div>
        <small className="muted-text">Prices come from your provider's pricing page; with them Agora shows the cost of each month. With a budget the agent stops answering once the month reaches it.</small>
        <div className="actions">
          {agent && (
            <button
              className="btn ghost danger push-left"
              type="button"
              disabled={busy}
              onClick={async () => {
                if (!confirm(`Delete ${agent.name}? It leaves every room. Its old answers stay in the rooms and its usage stays in this month's totals.`)) return;
                setBusy(true);
                setError(null);
                try {
                  await api(`/api/settings/agents/${agent.id}`, undefined, "DELETE");
                  onSaved();
                } catch (err) {
                  setError(err instanceof ApiError ? err.message : "Could not delete the agent.");
                  setBusy(false);
                }
              }}
            >
              Delete agent
            </button>
          )}
          <button className="btn ghost" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="submit" disabled={busy || !name.trim() || !model.trim()}>
            {agent ? "Save" : "Add agent"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
